import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Button from '@strava/ui/Button';
import AvatarGroup from '@strava/ui/AvatarGroup';
import Avatar from '@strava/ui/Avatar';
import I18n from 'utils/I18n';
import SvgActionsKudoHighlightedXsmall from '@strava/icons/ActionsKudoHighlightedXsmall';
import SvgActionsKudoNormalXsmall from '@strava/icons/ActionsKudoNormalXsmall';
import SvgActionsCommentNormalXsmall from '@strava/icons/ActionsCommentNormalXsmall';
import createNetworkingClient from 'utils/networking-client';
import {
  kudosAndCommentsProps,
  viewingAthleteProps,
  entityOwnerProps,
  segAndBestEffortAchievementsProps
} from '../../../props/feed-prop-types';
import styles from './styles.scss';
import MentionableCommentForm from '../../shared/MentionableCommentForm';
import KudosCommentsAndAchievementsModal from './KudosCommentsAndAchievementsModal';
import CommentsList from './CommentsList';

import { useFeedEntityContext } from '../../../state/FeedEntityContext';
import {
  OPEN_MODAL_COMMENTS_TAB,
  OPEN_MODAL_KUDOS_TAB,
  KUDO_STATUS_UPDATE_COMPLETED
} from '../../../state/feed-entity-actions';

const KudosAndComments = ({
  kudosAndComments,
  entity,
  entityId,
  ownedByCurrentAthlete,
  viewingAthlete,
  entityOwner,
  entityTitle,
  openCommentsModalOnIconClick,
  segAndBestEffortAchievements
}) => {
  const {
    canKudo,
    kudosCount,
    highlightedKudosers: initialLoadHighlightedKudosers,
    commentsEnabled,
    comments: initialLoadComments
  } = kudosAndComments;

  const [comments, setComments] = useState(initialLoadComments);
  const [canKudoStatus, setCanKudoStatus] = useState(canKudo);
  const [currentKudoCount, setCurrentKudoCount] = useState(kudosCount);
  const [showForm, setShowForm] = useState(false);
  const [highlightedKudosers, setHighlightedKudosers] = useState(
    initialLoadHighlightedKudosers
  );
  const { entityState, dispatch } = useFeedEntityContext();

  const prefix = 'components.feed.social';

  const commentIconButtonTitle = () => {
    return openCommentsModalOnIconClick
      ? I18n.t(`${prefix}.view_all_comments`)
      : I18n.t(`${prefix}.comments`);
  };

  const showKudosCallToAction =
    canKudoStatus && currentKudoCount === 0 && comments.length === 0;

  const kudoIconButtonTitle = () => {
    return showKudosCallToAction
      ? I18n.t(`${prefix}.give_kudos`)
      : I18n.t(`${prefix}.view_all_kudos`);
  };

  // Removes `strava:/` from a deeplink to make it a url
  // eg. `strava://athletes/123` changes to `/athletes/1234`
  const destinationFromDeepLink = (deeplink) => {
    return deeplink.replace('strava:/', '');
  };

  const showHighlightedKudoers = () => {
    return (
      <div className={styles.kudosFacePile} data-testid="avatar_group">
        <AvatarGroup>
          {highlightedKudosers.map((kudoer) => {
            return (
              <Avatar
                name={kudoer.show_name ? kudoer.display_name : ''}
                src={kudoer.avatar_url}
                href={destinationFromDeepLink(kudoer.destination_url)}
                type="athlete"
                size="xsmall"
                data-testid="highlight_avatar"
                key={kudoer.display_name}
              />
            );
          })}
        </AvatarGroup>
      </div>
    );
  };

  const handleClickKudoFail = () => {
    // TODO: handle error case
  };

  const onClickKudoSuccessful = useCallback(() => {
    // Not allowed to kudo more than once
    setCanKudoStatus(false);
    setCurrentKudoCount((prev) => prev + 1);

    const highlightedKudoser = {
      avatar_url: viewingAthlete.avatarUrl,
      destination_url: `/athletes/${viewingAthlete.id}`,
      display_name: viewingAthlete.name,
      show_name: false
    };
    setHighlightedKudosers((prev) => prev.concat(highlightedKudoser));
  }, [viewingAthlete.avatarUrl, viewingAthlete.id, viewingAthlete.name]);

  const onClickKudo = () => {
    createNetworkingClient()
      .post(`/feed/${entity.toLowerCase()}/${entityId}/kudo`)
      .then((response) => {
        if (response && response.status === 200) {
          onClickKudoSuccessful();
        } else {
          handleClickKudoFail();
        }
      })
      .catch(() => {
        handleClickKudoFail();
      });
  };

  useEffect(() => {
    if (entityState.kudoPosted) {
      onClickKudoSuccessful();
      dispatch({ type: KUDO_STATUS_UPDATE_COMPLETED });
    }
  }, [entityState.kudoPosted, onClickKudoSuccessful, dispatch]);

  const onClickComment = () => {
    setShowForm((prev) => !prev);
  };

  // eg. "45 kudos. 12 comments"
  const kudosAndCommentsCtaText = () => {
    const components = [];
    // kudo label text when there are no kudos (Be the first to give kudos!)
    if (showKudosCallToAction) {
      components.push(
        <Button
          type="button"
          variant="text"
          className={styles.countButton}
          onClick={
            canKudoStatus
              ? onClickKudo
              : () => dispatch({ type: OPEN_MODAL_KUDOS_TAB })
          }
          data-testid="give_kudos_button"
          key="give_kudos_button"
        >
          {I18n.t('components.feed.social.give_kudos')}
        </Button>
      );
    }
    // kudo label text with count (eg. 77 kudos)
    else if (currentKudoCount > 0) {
      components.push(
        <Button
          type="button"
          variant="text"
          className={styles.countButton}
          onClick={() => dispatch({ type: OPEN_MODAL_KUDOS_TAB })}
          data-testid="kudos_count"
          key="kudos_count"
        >
          {currentKudoCount}&nbsp;
          {I18n.t('components.feed.social.kudos')}
        </Button>
      );
      if (commentsEnabled && comments.length > 0) {
        components.push(
          <div className={styles.spacer} key="spacer" data-testid="spacer">
            ·
          </div>
        );
      }
    }
    // comment label text with count (eg. 48 comments )
    if (commentsEnabled && comments.length > 0) {
      components.push(
        <Button
          type="button"
          variant="text"
          className={styles.countButton}
          onClick={() => dispatch({ type: OPEN_MODAL_COMMENTS_TAB })}
          data-testid="comments_count"
          key="comments_count"
        >
          {comments.length}&nbsp;
          {comments.length === 1
            ? I18n.t('components.feed.social.comment')
            : I18n.t('components.feed.social.comments')}
        </Button>
      );
    }
    return (
      <div className={styles.countWrapper} data-testid="counts_wrapper">
        {components}
      </div>
    );
  };

  const kudosAndCommentsIconButtons = () => {
    return (
      <>
        {/* Kudos thumb icon button */}
        <Button
          type="button"
          variant="default"
          className={styles.socialButton}
          title={kudoIconButtonTitle()}
          onClick={
            canKudoStatus
              ? onClickKudo
              : () => dispatch({ type: OPEN_MODAL_KUDOS_TAB })
          }
          data-testid="kudos_button"
        >
          {canKudoStatus || ownedByCurrentAthlete ? (
            <SvgActionsKudoNormalXsmall data-testid="unfilled_kudos" />
          ) : (
            <SvgActionsKudoHighlightedXsmall
              fill="#fc5200"
              data-testid="filled_kudos"
            />
          )}

          {/* Comments icon button */}
        </Button>
        {commentsEnabled && (
          <Button
            type="button"
            variant="default"
            className={styles.socialButton}
            data-testid="comment_button"
            onClick={onClickComment}
            title={commentIconButtonTitle()}
          >
            <SvgActionsCommentNormalXsmall />
          </Button>
        )}
      </>
    );
  };

  useEffect(() => {
    if (entityState.newComment) {
      setComments((prev) => prev.concat(entityState.newComment));
    }
  }, [entityState.newComment]);

  const mentionableCommentsForm = () => (
    <form className={styles.media}>
      <div className={styles.mediaContent}>
        <MentionableCommentForm
          viewingAthlete={viewingAthlete}
          entity={entity.toLowerCase()}
          entityId={entityId}
          autoFocus={true}
        />
      </div>
    </form>
  );

  const removeCommentFromList = (commentId) => {
    setComments((prev) =>
      prev.filter((comment) => comment.comment_id !== commentId)
    );
  };

  return (
    <>
      <div
        className={styles.kudosAndComments}
        data-testid="kudos_comments_container"
      >
        <div className={styles.mediaBody}>
          {highlightedKudosers.length > 0 && showHighlightedKudoers()}
          {kudosAndCommentsCtaText()}
        </div>
        <div className={styles.mediaActions}>
          {kudosAndCommentsIconButtons()}
        </div>
      </div>
      <div className={styles.entryComments}>
        {commentsEnabled && (
          <CommentsList
            comments={comments}
            onSeeMoreCommentsClick={() =>
              dispatch({ type: OPEN_MODAL_COMMENTS_TAB })
            }
            onCommentDelete={(commentId) => removeCommentFromList(commentId)}
          />
        )}
        {showForm && mentionableCommentsForm()}
      </div>

      {entityState.openKudosCommentModal && (
        <KudosCommentsAndAchievementsModal
          comments={comments}
          entity={entity}
          entityId={entityId}
          entityOwner={entityOwner}
          hasOtherAthletes={false}
          title={entityTitle}
          viewingAthlete={viewingAthlete}
          kudosCount={currentKudoCount}
          commentsEnabled={commentsEnabled}
          segAndBestEffortAchievements={segAndBestEffortAchievements}
        />
      )}
    </>
  );
};

KudosAndComments.propTypes = {
  kudosAndComments: kudosAndCommentsProps.isRequired,
  entity: PropTypes.string.isRequired,
  entityId: PropTypes.string.isRequired,
  ownedByCurrentAthlete: PropTypes.bool.isRequired,
  viewingAthlete: viewingAthleteProps.isRequired,
  entityOwner: entityOwnerProps.isRequired,
  entityTitle: PropTypes.string.isRequired,
  segAndBestEffortAchievements: segAndBestEffortAchievementsProps,
  // (openCommentsModalOnIconClick) controls whether the kudos and comments modal is opened
  // directly by the social action buttons. This is used by group activities for example
  openCommentsModalOnIconClick: PropTypes.bool.isRequired
};

KudosAndComments.defaultProps = {
  segAndBestEffortAchievements: []
};

export default KudosAndComments;
